/**
 * The seven sidebar sections (FEED_SPEC § Filter sidebar).
 *
 * Every one takes **value + onChange and nothing else** — no feed query, no ui-state handle, no
 * router. That is deliberate: the view screens can mount the same sections (minus Groups/Profiles)
 * in a later pass without a rewrite, and it keeps each section testable by inspection.
 *
 * The read-only counted-types echo the Filter popover carries is dropped here: it belongs to the
 * Metrics contexts where those types actually move a number.
 */

import { useState } from 'react';
import {
  CONTENT_TYPES,
  LENGTH_UNITS,
  MAX_PERF_CONDITIONS,
  METRIC_KEYS,
  POST_TYPES,
  type ContentType,
  type GroupDTO,
  type LengthUnit,
  type MetricKey,
  type PerfConditionDTO,
  type PerfKind,
  type PostType,
  type ProfileRowDTO,
} from '@lw/shared';
import { Avatar, CheckboxList, CloseIcon, PlusIcon, SearchIcon, cx } from '../../components';
import { pluralize } from '../../lib/format';
import { CONTENT_TYPE_LABEL, METRIC_LABEL, POST_TYPE_LABEL } from '../../lib/labels';

/** Shared search box of the two list sections; filtering is client-side over an already-loaded list. */
function SectionSearch({
  value,
  onChange,
  placeholder,
  label,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  label: string;
}) {
  return (
    <div className="sb-search">
      <SearchIcon size={13} />
      <input
        className="sb-search__input"
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={label}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

/* ----------------------------------------------------------------- groups */

export type GroupsSectionProps = {
  groups: readonly GroupDTO[];
  selected: readonly number[];
  onChange: (next: number[]) => void;
};

export function FeedGroupsSection({ groups, selected, onChange }: GroupsSectionProps) {
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  const matches = groups
    .filter((group) => needle === '' || group.name.toLowerCase().includes(needle))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      <SectionSearch
        value={search}
        onChange={setSearch}
        placeholder="Search groups…"
        label="Search groups"
      />
      <CheckboxList
        options={matches.map((group) => ({
          value: group.id,
          label: group.name,
          sub: pluralize(group.memberCount, 'member'),
        }))}
        selected={selected}
        onChange={onChange}
        emptyLabel={groups.length === 0 ? 'No groups yet.' : 'No group matches that.'}
      />
    </>
  );
}

/* --------------------------------------------------------------- profiles */

export type ProfilesSectionProps = {
  /**
   * `/feed` passes active profiles only — archived ones are out of its scope entirely (FEED_SPEC).
   * `/saved` passes every profile, because a save outlives archiving (§6.6), so the rows carry an
   * "Archived" marker rather than assuming they cannot appear.
   */
  profiles: readonly ProfileRowDTO[];
  selected: readonly number[];
  onChange: (next: number[]) => void;
  mediaEnabled: boolean;
};

export function FeedProfilesSection({
  profiles,
  selected,
  onChange,
  mediaEnabled,
}: ProfilesSectionProps) {
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  const nameOf = (profile: ProfileRowDTO): string => profile.name ?? profile.slug;
  // Search matches the note as well as the name: the note is where the user wrote why they watch
  // this profile, so it is the second thing they would search by.
  const matches = profiles
    .filter(
      (profile) =>
        needle === '' ||
        nameOf(profile).toLowerCase().includes(needle) ||
        (profile.notes ?? '').toLowerCase().includes(needle),
    )
    .sort((a, b) => nameOf(a).localeCompare(nameOf(b)));

  return (
    <>
      <SectionSearch
        value={search}
        onChange={setSearch}
        placeholder="Search profiles…"
        label="Search profiles"
      />
      <CheckboxList
        options={matches.map((profile) => ({
          value: profile.id,
          label: nameOf(profile),
          lead: (
            <Avatar
              media={profile.avatar}
              name={profile.name}
              mediaEnabled={mediaEnabled}
              size="sm"
              kind={profile.kind}
            />
          ),
          ...(profile.notes === null || profile.notes.length === 0
            ? {}
            : { sub: profile.notes }),
          ...(profile.archivedAt === null || profile.archivedAt === undefined
            ? {}
            : { hint: <span className="faint">Archived</span> }),
        }))}
        selected={selected}
        onChange={onChange}
        emptyLabel={profiles.length === 0 ? 'No profiles yet.' : 'No profile matches that.'}
      />
    </>
  );
}

/* ------------------------------------------------------------ type lists */

export function FeedPostTypesSection({
  selected,
  onChange,
  countedPostTypes,
}: {
  selected: readonly PostType[];
  onChange: (next: PostType[]) => void;
  countedPostTypes: readonly PostType[];
}) {
  return (
    <CheckboxList
      options={POST_TYPES.map((type) => ({
        value: type,
        label: POST_TYPE_LABEL[type],
        ...(countedPostTypes.includes(type) ? { hint: 'counted' } : {}),
      }))}
      selected={selected}
      onChange={onChange}
    />
  );
}

export function FeedContentTypesSection({
  selected,
  onChange,
}: {
  selected: readonly ContentType[];
  onChange: (next: ContentType[]) => void;
}) {
  return (
    <CheckboxList
      options={CONTENT_TYPES.map((type) => ({ value: type, label: CONTENT_TYPE_LABEL[type] }))}
      selected={selected}
      onChange={onChange}
    />
  );
}

/* ------------------------------------------------------------ performance */

/**
 * Up to eight AND-combined conditions, one per row (§5.2 `perf=`).
 *
 * Every row is committed the moment it appears — there is no draft state to reconcile, so "+ Add
 * filter" simply appends the first (metric, kind) pair not already in use. Min-only by design: a
 * maximum was never asked for, and `≥` is the whole vocabulary of "what performed well".
 */
export type PerformanceValue = readonly PerfConditionDTO[];

const PERF_KIND_STATES: { kind: PerfKind; glyph: string; label: string }[] = [
  { kind: 'multiplier', glyph: '×', label: 'Baseline multiple' },
  { kind: 'raw', glyph: '#', label: 'Raw count' },
];

/** Sensible starting threshold per kind: 2× a baseline, or a flat 10 of the metric. */
const PERF_DEFAULT_MIN: Record<PerfKind, number> = { multiplier: 2, raw: 10 };

/** First (metric, kind) pair still free, walking multipliers before raw counts. */
function nextPerfCondition(used: PerformanceValue): PerfConditionDTO | null {
  for (const { kind } of PERF_KIND_STATES) {
    for (const metric of METRIC_KEYS) {
      const taken = used.some(
        (condition) => condition.metric === metric && condition.kind === kind,
      );
      if (!taken) return { metric, kind, min: PERF_DEFAULT_MIN[kind] };
    }
  }
  return null;
}

export function FeedPerformanceSection({
  value,
  onChange,
}: {
  value: PerformanceValue;
  onChange: (next: PerfConditionDTO[]) => void;
}) {
  // An edit that collides with another row wins it: the duplicate goes, rather than two rows
  // claiming the same (metric, kind) and the server joining `baselines` twice for one condition.
  const replaceAt = (index: number, next: PerfConditionDTO) => {
    onChange(
      value
        .map((condition, position) => (position === index ? next : condition))
        .filter(
          (condition, position) =>
            position === index ||
            !(condition.metric === next.metric && condition.kind === next.kind),
        ),
    );
  };

  const removeAt = (index: number) =>
    onChange(value.filter((_, position) => position !== index));

  const addable = value.length < MAX_PERF_CONDITIONS ? nextPerfCondition(value) : null;

  return (
    <div className="sb-field">
      {value.map((condition, index) => (
        // Keyed by position, not by (metric, kind): a row *is* its slot, and keying by its value
        // would unmount the row the moment its select changed — taking the focus with it.
        <div className="sb-perf" key={index}>
          <select
            className="select sb-perf__metric"
            aria-label="Performance metric"
            value={condition.metric}
            onChange={(event) =>
              replaceAt(index, { ...condition, metric: event.target.value as MetricKey })
            }
          >
            {METRIC_KEYS.map((metric) => (
              <option key={metric} value={metric}>
                {METRIC_LABEL[metric]}
              </option>
            ))}
          </select>

          <div className="sb-seg" role="group" aria-label="Threshold kind">
            {PERF_KIND_STATES.map((state) => (
              <button
                key={state.kind}
                type="button"
                className={cx('sb-seg__btn', condition.kind === state.kind && 'sb-seg__btn--on')}
                aria-label={state.label}
                aria-pressed={condition.kind === state.kind}
                onClick={() =>
                  replaceAt(index, {
                    ...condition,
                    kind: state.kind,
                    // Switching kinds keeps the row meaningful: 2 comments is not 2× comments.
                    min: PERF_DEFAULT_MIN[state.kind],
                  })
                }
              >
                {state.glyph}
              </button>
            ))}
          </div>

          <span className="muted">≥</span>
          <input
            className="input sb-perf__min"
            type="number"
            step={condition.kind === 'multiplier' ? '0.5' : '1'}
            min="0"
            aria-label={condition.kind === 'multiplier' ? 'Minimum multiplier' : 'Minimum count'}
            value={condition.min}
            onChange={(event) => {
              // A row always has a threshold — an emptied (or unparseable) field reads as 0, never
              // as `NaN`, which the stored blob's schema would reject on the next write.
              const parsed = Number(event.target.value);
              replaceAt(index, {
                ...condition,
                min: Number.isFinite(parsed) ? Math.max(0, parsed) : 0,
              });
            }}
          />
          <button
            type="button"
            className="icon-btn sb-perf__remove"
            aria-label={`Remove ${METRIC_LABEL[condition.metric]} condition`}
            onClick={() => removeAt(index)}
          >
            <CloseIcon size={11} />
          </button>
        </div>
      ))}

      {addable === null ? null : (
        <button
          type="button"
          className="btn-ghost btn-ghost--sm sb-perf__add"
          onClick={() => onChange([...value, addable])}
        >
          <PlusIcon size={12} />
          Add filter
        </button>
      )}

      <p className="field__hint">
        <strong>×</strong> compares a post to its own author's baseline; <strong>#</strong> is the
        plain count. Conditions combine with AND; filtering never changes a baseline.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------- attributes */

export type AttributesValue = { hasMedia?: boolean; edited?: boolean; hasLink?: boolean };
type TriKey = keyof AttributesValue;

const TRI_STATES: { id: 'any' | 'yes' | 'no'; label: string }[] = [
  { id: 'any', label: 'Any' },
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
];

const ATTRIBUTES: { key: TriKey; label: string }[] = [
  { key: 'hasMedia', label: 'Media' },
  { key: 'edited', label: 'Edited' },
  { key: 'hasLink', label: 'Link' },
];

export function FeedAttributesSection({
  value,
  onChange,
}: {
  value: AttributesValue;
  onChange: (next: AttributesValue) => void;
}) {
  // "Any" removes the param entirely rather than sending a third state (§5.2).
  const set = (key: TriKey, state: 'any' | 'yes' | 'no') => {
    const next: AttributesValue = { ...value };
    if (state === 'any') delete next[key];
    else next[key] = state === 'yes';
    onChange(next);
  };

  return (
    <div className="sb-field">
      {ATTRIBUTES.map((attribute) => {
        const current = value[attribute.key];
        const active = current === undefined ? 'any' : current ? 'yes' : 'no';
        return (
          <div className="sb-tri" key={attribute.key}>
            <span className="sb-tri__label">{attribute.label}</span>
            <div className="sb-seg" role="group" aria-label={attribute.label}>
              {TRI_STATES.map((state) => (
                <button
                  key={state.id}
                  type="button"
                  className={cx('sb-seg__btn', active === state.id && 'sb-seg__btn--on')}
                  aria-pressed={active === state.id}
                  onClick={() => set(attribute.key, state.id)}
                >
                  {state.label}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ----------------------------------------------------------------- length */

export type LengthValue = { unit?: LengthUnit; min?: number; max?: number };

export function FeedLengthSection({
  value,
  onChange,
}: {
  value: LengthValue;
  onChange: (next: LengthValue) => void;
}) {
  const set = (unit: LengthUnit, min: string, max: string) => {
    const parse = (raw: string): number | undefined => {
      if (raw.trim() === '') return undefined;
      const parsed = Math.max(0, Math.trunc(Number(raw)));
      return Number.isNaN(parsed) ? undefined : parsed;
    };
    const parsedMin = parse(min);
    const parsedMax = parse(max);
    // The unit survives an empty range: emitting `{}` here is what used to snap a chosen
    // "Characters" straight back to "Words". A unit alone is not a filter — nothing counts or
    // sends it — but it is a remembered choice.
    if (parsedMin === undefined && parsedMax === undefined) {
      onChange({ unit });
      return;
    }
    const next: LengthValue = { unit };
    if (parsedMin !== undefined) next.min = parsedMin;
    if (parsedMax !== undefined) next.max = parsedMax;
    onChange(next);
  };

  const unit = value.unit ?? 'words';

  return (
    <div className="sb-field">
      <div className="sb-seg" role="group" aria-label="Length unit">
        {LENGTH_UNITS.map((candidate) => (
          <button
            key={candidate}
            type="button"
            className={cx('sb-seg__btn', unit === candidate && 'sb-seg__btn--on')}
            aria-pressed={unit === candidate}
            onClick={() => set(candidate, String(value.min ?? ''), String(value.max ?? ''))}
          >
            {candidate === 'words' ? 'Words' : 'Characters'}
          </button>
        ))}
      </div>
      <div className="sb-row">
        <input
          className="input input--narrow"
          type="number"
          min="0"
          placeholder="min"
          aria-label="Minimum length"
          value={value.min ?? ''}
          onChange={(event) => set(unit, event.target.value, String(value.max ?? ''))}
        />
        <span className="muted">–</span>
        <input
          className="input input--narrow"
          type="number"
          min="0"
          placeholder="max"
          aria-label="Maximum length"
          value={value.max ?? ''}
          onChange={(event) => set(unit, String(value.min ?? ''), event.target.value)}
        />
      </div>
    </div>
  );
}
